import { Progress } from "@/components/ui/progress";
import { formatToBRL } from "@/utils/formatToMoney";
import {
  LucideIcon,
  RotateCcw,
  TrendingDown,
  UserCheck,
  Wallet,
} from "lucide-react";
import { PRIMARY_HEX } from "../constants";
import {
  useGetDashboardCycleSummaryQuery,
  useGetDashboardSummaryQuery,
  type DashboardCardResponse,
} from "@/services/conciliation-dashboard";
import { DASHBOARD_LABELS } from "../messages";
import { DateRange, SummaryBreakdownItem } from "../types";

const PERCENTAGE_BASE: number = 100;

const POSITIVE_DELTA_CLASS_NAME: string = "text-green-600";

const NEUTRAL_DELTA_CLASS_NAME: string = "text-amber-600";

interface SummaryCardProps {
  title: string;
  Icon: LucideIcon;
  total: number;
  delta: string;
  deltaClassName: string;
  deltaTitle: string;
  breakdown: readonly SummaryBreakdownItem[];
  progress?: number;
  titleTooltip?: string;
}

const percentage = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * PERCENTAGE_BASE) : 0;

function SummaryCard({
  title,
  Icon,
  total,
  delta,
  deltaClassName,
  deltaTitle,
  breakdown,
  progress,
  titleTooltip,
}: SummaryCardProps): JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" color={PRIMARY_HEX} />
        <p className="text-sm font-medium text-gray-600" title={titleTooltip}>
          {title}
        </p>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-[#613F4F]">{total}</span>
        <span
          className={`text-xs font-medium ${deltaClassName}`}
          title={deltaTitle}
        >
          {delta}
        </span>
      </div>
      <div className="mt-3 space-y-1">
        {breakdown.map((item: SummaryBreakdownItem) => (
          <div
            key={item.label}
            className="flex justify-between text-xs"
            title={item.title}
          >
            <span className="text-gray-500">{item.label}</span>
            <span
              className={item.highlighted ? "text-amber-600" : "text-gray-700"}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
      {progress !== undefined && (
        <Progress value={progress} className="mt-3 h-2" />
      )}
    </div>
  );
}

const EMPTY_CARD: DashboardCardResponse = {
  total: 0,
  resolved: 0,
  pending: 0,
  resolved_percentage: 0,
  delta_percentage: 0,
};

const formatDelta = (value: number): string =>
  `${value > 0 ? "+" : ""}${value}%`;

interface SummaryCardsProps {
  range: DateRange;
}

export function SummaryCards({ range }: SummaryCardsProps): JSX.Element {
  const { data: summary } = useGetDashboardSummaryQuery(range);
  const { data: cycle } = useGetDashboardCycleSummaryQuery(range);

  const losses: DashboardCardResponse = summary?.losses ?? EMPTY_CARD;
  const payrollDiscount: DashboardCardResponse =
    summary?.payroll_discount ?? EMPTY_CARD;
  const hrApproval: DashboardCardResponse = summary?.hr_approval ?? EMPTY_CARD;

  const comparisonTooltip: string = DASHBOARD_LABELS.COMPARISON_TOOLTIP(
    cycle?.previous_cycle_interval ?? "",
  );

  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        title={DASHBOARD_LABELS.CARD_LOSSES}
        Icon={TrendingDown}
        total={losses.total}
        delta={formatDelta(losses.delta_percentage)}
        deltaClassName={POSITIVE_DELTA_CLASS_NAME}
        deltaTitle={DASHBOARD_LABELS.LOSSES_DELTA_TOOLTIP}
        breakdown={[
          {
            label: DASHBOARD_LABELS.RESOLVED,
            value: `${losses.resolved} (${percentage(
              losses.resolved,
              losses.total,
            )}%)`,
            highlighted: false,
          },
          {
            label: DASHBOARD_LABELS.PENDING,
            value: `${losses.pending}`,
            highlighted: true,
          },
        ]}
        progress={percentage(losses.resolved, losses.total)}
      />

      <SummaryCard
        title={DASHBOARD_LABELS.CARD_PAYROLL_DISCOUNT}
        Icon={Wallet}
        total={payrollDiscount.total}
        delta={formatDelta(payrollDiscount.delta_percentage)}
        deltaClassName={POSITIVE_DELTA_CLASS_NAME}
        deltaTitle={comparisonTooltip}
        titleTooltip={DASHBOARD_LABELS.PAYROLL_DISCOUNT_TOOLTIP}
        breakdown={[
          {
            label: DASHBOARD_LABELS.RESOLVED_MALE,
            value: `${payrollDiscount.resolved} (${percentage(
              payrollDiscount.resolved,
              payrollDiscount.total,
            )}%)`,
            highlighted: false,
          },
          {
            label: DASHBOARD_LABELS.PENDING,
            value: `${payrollDiscount.pending}`,
            highlighted: true,
          },
        ]}
        progress={percentage(payrollDiscount.resolved, payrollDiscount.total)}
      />

      <SummaryCard
        title={DASHBOARD_LABELS.CARD_REVERSALS}
        Icon={RotateCcw}
        total={summary?.reversals.total ?? 0}
        delta=""
        deltaClassName={NEUTRAL_DELTA_CLASS_NAME}
        deltaTitle={comparisonTooltip}
        breakdown={[
          {
            label: DASHBOARD_LABELS.REVERSAL_PAYROLL_DISCOUNT,
            value: formatToBRL(
              Number(summary?.reversals.payroll_discount_amount ?? 0),
            ),
            highlighted: false,
          },
          {
            label: DASHBOARD_LABELS.REVERSAL_LOSS,
            value: formatToBRL(Number(summary?.reversals.loss_amount ?? 0)),
            highlighted: false,
          },
          {
            label: DASHBOARD_LABELS.REVERSAL_PERIOD_AVERAGE,
            value: formatToBRL(
              Number(summary?.reversals.previous_cycles_average ?? 0),
            ),
            highlighted: false,
            title: DASHBOARD_LABELS.REVERSAL_AVERAGE_TOOLTIP(
              summary?.reversals.previous_cycles_considered ?? 0,
            ),
          },
        ]}
      />

      <SummaryCard
        title={DASHBOARD_LABELS.CARD_HR_APPROVAL}
        Icon={UserCheck}
        total={hrApproval.total}
        delta={formatDelta(hrApproval.delta_percentage)}
        deltaClassName={POSITIVE_DELTA_CLASS_NAME}
        deltaTitle={comparisonTooltip}
        breakdown={[
          {
            label: DASHBOARD_LABELS.ANSWERED,
            value: `${hrApproval.resolved} (${percentage(
              hrApproval.resolved,
              hrApproval.total,
            )}%)`,
            highlighted: false,
          },
          {
            label: DASHBOARD_LABELS.WAITING,
            value: `${hrApproval.pending}`,
            highlighted: true,
          },
        ]}
        progress={percentage(hrApproval.resolved, hrApproval.total)}
      />
    </div>
  );
}
